import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Wand2, X, Monitor, Smartphone, Square, Maximize } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { EditorSegment } from '../../types/editor';
import { jobStatusLabel, toMediaUrl, waitForJobResultUrl } from '../../lib/jobsApi';
import VideoModelPicker from '../video/VideoModelPicker';
import { estimateCredits, pickVideoModelSelection, rateForGeneratedAudio, supportsGeneratedAudio } from '../../lib/videoModelCatalog';
import { useFeatureVideoPick } from '../../lib/videoFeatureUsage';

const MIN_DURATION = 3;
const MAX_DURATION = 10;
/** Omni V2V (esp. 10s / pro) often exceeds 5 minutes — poll up to ~15 min. */
const V2V_POLL_INTERVAL_MS = 5000;
const V2V_MAX_POLLS = 180;

type AspectChoice = 'auto' | '16:9' | '9:16' | '1:1';

interface Props {
  segment: EditorSegment;
  onClose: () => void;
  onVideoReady: (segmentId: string, videoUrl: string, label: string) => void;
  getUserOrgId: () => Promise<string | null>;
  prepareClipBlob: (segment: EditorSegment) => Promise<Blob>;
  uploadBaseVideo: (orgId: string, blob: Blob) => Promise<string>;
}

export default function KlingV2VPanel({
  segment,
  onClose,
  onVideoReady,
  getUserOrgId,
  prepareClipBlob,
  uploadBaseVideo,
}: Props) {
  const [prompt, setPrompt] = useState('');
  const [keepAudio, setKeepAudio] = useState(true);
  const [aspectRatio, setAspectRatio] = useState<AspectChoice>('auto');
  const {
    value: videoPick,
    setValue: setVideoPick,
    recommendedModelId: v2vRecommendedModelId,
    useCount: v2vModelUseCount,
  } = useFeatureVideoPick('v2v', 'v2v');
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const scroller = document.querySelector('.content-wrapper') as HTMLElement | null;
    const prev = scroller?.style.overflow ?? '';
    if (scroller) scroller.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      if (scroller) scroller.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [loading, onClose]);

  const effectiveDuration = useMemo(() => {
    const raw = Math.max(0.1, segment.duration / (segment.speed || 1));
    return Math.round(raw * 10) / 10;
  }, [segment.duration, segment.speed]);

  const durationOk = effectiveDuration >= MIN_DURATION && effectiveDuration <= MAX_DURATION;
  const isVideo = segment.type === 'video' || segment.type === 'ai_generated';
  const billableDuration = Math.max(MIN_DURATION, Math.min(MAX_DURATION, Math.round(effectiveDuration)));

  const selection = pickVideoModelSelection({
    videoModelId: videoPick.videoModelId,
    resolutionId: videoPick.resolutionId,
    modality: 'v2v',
  });
  const billedV2v = Boolean(selection.model.billedOutputPlusRef);
  const v2vRate = rateForGeneratedAudio(
    selection.resolution,
    Boolean(videoPick.generateAudio) && supportsGeneratedAudio(selection.model),
  );
  const creditsEstimate = estimateCredits({
    creditsPerSecond: v2vRate.creditsPerSecond,
    durationSeconds: billableDuration,
    referenceDurationSeconds: billableDuration,
    billedOutputPlusRef: billedV2v,
  });

  const handleSubmit = async () => {
    if (!prompt.trim() || loading) return;
    if (!isVideo) {
      setError('V2V funciona apenas com clips de vídeo (não imagens).');
      return;
    }
    if (!durationOk) {
      setError(`O clip precisa ter entre ${MIN_DURATION}s e ${MAX_DURATION}s. Duração atual: ${effectiveDuration}s. Use Cortar na timeline.`);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const orgId = await getUserOrgId();
      if (!orgId) throw new Error('Organização não encontrada.');

      setLoadingStep('Preparando o trecho do vídeo...');
      const clipBlob = await prepareClipBlob(segment);

      setLoadingStep('Enviando vídeo base...');
      const baseVideoUrl = await uploadBaseVideo(orgId, clipBlob);

      setLoadingStep('Iniciando a edição...');
      const body: Record<string, unknown> = {
        mode: 'v2v',
        prompt: prompt.trim(),
        base_video_url: baseVideoUrl,
        organization_id: orgId,
        duration_seconds: billableDuration,
        reference_duration_seconds: billableDuration,
        keep_original_sound: keepAudio,
        video_model_id: videoPick.videoModelId,
        resolution_id: videoPick.resolutionId,
        feature: 'v2v',
        generate_audio: Boolean(videoPick.generateAudio),
      };
      if (aspectRatio !== 'auto') {
        body.aspect_ratio = aspectRatio;
      }

      let response = await supabase.functions.invoke('create-video', { body });
      if (response.error) throw new Error(response.error.message);
      let data = response.data;
      if (data?.error) throw new Error(data.error);

      if (data?.async && data.job_id) {
        if (data.mode !== 'v2v' || !data.generation_id) {
          throw new Error(
            'O servidor não entrou no modo V2V (deploy desatualizado). Faça deploy da edge function create-video e tente de novo.',
          );
        }
        setLoadingStep('Na fila — acompanhe o progresso');
        const url = await waitForJobResultUrl(data.job_id, {
          timeoutMs: 20 * 60 * 1000,
          onStatus: (j) => setLoadingStep(jobStatusLabel(j)),
        });
        onVideoReady(segment.id, toMediaUrl(url), prompt.trim().slice(0, 40) || 'Edição V2V');
        onClose();
        return;
      }

      if (!data?.success || !data?.task_id) throw new Error('Falha ao iniciar edição V2V.');
      if (data.mode !== 'v2v' || !data.generation_id) {
        throw new Error(
          'O servidor não entrou no modo V2V (deploy desatualizado). Faça deploy da edge function create-video e tente de novo.',
        );
      }

      const taskId = data.task_id as string;
      const generationId = data.generation_id as string | undefined;

      let attempts = 0;
      let lastStatus: string | undefined = data?.status;
      while ((data?.status === 'submitted' || data?.status === 'processing') && attempts < V2V_MAX_POLLS) {
        attempts++;
        setLoadingStep(`Gerando o vídeo... (${attempts * (V2V_POLL_INTERVAL_MS / 1000)}s)`);
        await new Promise(r => setTimeout(r, V2V_POLL_INTERVAL_MS));
        const pollResp = await supabase.functions.invoke('create-video', {
          body: {
            task_id: taskId,
            generation_id: generationId,
            organization_id: orgId,
          },
        });
        if (pollResp.error) throw new Error(pollResp.error.message);
        data = pollResp.data;
        if (data?.error) throw new Error(data.error);
        lastStatus = data?.status;
      }

      if (data?.success && data.final_video) {
        onVideoReady(segment.id, data.final_video, prompt.trim().slice(0, 40) || 'Edição V2V');
        onClose();
      } else {
        throw new Error(
          data?.error ||
            `Edição V2V não concluída a tempo (status: ${lastStatus || 'desconhecido'} após ~${attempts * (V2V_POLL_INTERVAL_MS / 1000)}s). Tente de novo — jobs longos podem levar até ~15 min.`,
        );
      }
    } catch (err: any) {
      setError(err?.message || 'Erro na edição V2V.');
    } finally {
      setLoading(false);
      setLoadingStep('');
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Editar com IA V2V"
      style={{
        position: 'fixed', inset: 0, zIndex: 9600,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 440,
        background: 'rgba(14,14,20,0.98)',
        border: '1px solid rgba(168,85,247,0.25)',
        borderRadius: 14,
        boxShadow: '0 20px 50px rgba(0,0,0,0.55)',
        padding: 16,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Wand2 size={16} style={{ color: '#c084fc' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 650, color: '#e9d5ff' }}>Editar com IA V2V</div>
            <div style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.4)' }}>
              Escolha o modelo e a qualidade — veja o custo em cr/s
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', cursor: loading ? 'not-allowed' : 'pointer', padding: 4, display: 'flex' }}
          >
            <X size={16} />
          </button>
        </div>

        {segment.thumbnailUrl && (
          <img
            src={segment.thumbnailUrl}
            alt={segment.label}
            style={{
              width: '100%', maxHeight: 140, objectFit: 'cover',
              borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: '#000',
            }}
          />
        )}

        <VideoModelPicker
          modality="v2v"
          value={{ ...videoPick, durationSeconds: billableDuration }}
          onChange={setVideoPick}
          durationSeconds={billableDuration}
          referenceDurationSeconds={billableDuration}
          recommendedModelId={v2vModelUseCount > 0 ? v2vRecommendedModelId : null}
          compact
        />

        <div style={{
          display: 'flex', justifyContent: 'space-between', gap: 8,
          fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)',
        }}>
          <span>{segment.label || 'Clip'}</span>
          <span style={{ color: durationOk ? '#86efac' : '#f87171' }}>
            {effectiveDuration}s {durationOk ? '' : `(exige ${MIN_DURATION}–${MAX_DURATION}s)`}
          </span>
        </div>

        <div>
          <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>
            O que deve mudar neste trecho?
          </div>
          <textarea
            rows={4}
            value={prompt}
            disabled={loading}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Descreva qualquer mudança no clip (luz, clima, objetos, estilo, etc.)"
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'rgba(0,0,0,0.35)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8, padding: '8px 10px',
              color: '#fff', fontSize: '0.75rem', resize: 'vertical',
              fontFamily: 'inherit', outline: 'none',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)' }}>Orientação:</span>
          {([
            { v: 'auto' as const, l: 'Do clip', i: <Maximize size={10} /> },
            { v: '16:9' as const, l: 'Landscape', i: <Monitor size={10} /> },
            { v: '9:16' as const, l: 'Portrait', i: <Smartphone size={10} /> },
            { v: '1:1' as const, l: '1:1', i: <Square size={10} /> },
          ]).map(o => (
            <button
              key={o.v}
              type="button"
              disabled={loading}
              onClick={() => setAspectRatio(o.v)}
              style={{
                padding: '3px 8px', borderRadius: 4,
                border: `1px solid ${aspectRatio === o.v ? '#a855f7' : 'rgba(255,255,255,0.06)'}`,
                fontSize: '0.6rem', fontFamily: 'inherit',
                background: aspectRatio === o.v ? 'rgba(168,85,247,0.15)' : 'transparent',
                color: aspectRatio === o.v ? '#d8b4fe' : 'rgba(255,255,255,0.5)',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 3,
              }}
            >
              {o.i} {o.l}
            </button>
          ))}
        </div>

        {selection.model.provider === 'kling' && (
        <label style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: '0.68rem', color: 'rgba(255,255,255,0.65)', cursor: loading ? 'not-allowed' : 'pointer',
        }}>
          <input
            type="checkbox"
            checked={keepAudio}
            disabled={loading}
            onChange={(e) => setKeepAudio(e.target.checked)}
          />
          Manter áudio original do clip
        </label>
        )}

        <div style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)' }}>
          Duração do clip ~{billableDuration}s · Total estimado ~{creditsEstimate} créditos
        </div>

        {loading && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: 8,
            background: 'rgba(168,85,247,0.08)', borderRadius: 8,
          }}>
            <div style={{
              width: 12, height: 12, border: '2px solid #a855f7', borderTopColor: 'transparent',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.68rem', color: '#c084fc' }}>{loadingStep}</span>
          </div>
        )}

        {error && (
          <div style={{
            padding: 8, background: 'rgba(239,68,68,0.08)', borderRadius: 8,
            border: '1px solid rgba(239,68,68,0.2)', fontSize: '0.65rem', color: '#f87171',
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{
              background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8, padding: '7px 12px', color: 'rgba(255,255,255,0.6)',
              fontSize: '0.7rem', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || !prompt.trim() || !isVideo || !durationOk}
            style={{
              background: prompt.trim() && isVideo && durationOk ? 'linear-gradient(135deg, #7c3aed, #a855f7)' : 'rgba(255,255,255,0.06)',
              border: 'none', borderRadius: 8, padding: '7px 14px',
              color: '#fff', fontSize: '0.7rem', fontWeight: 600,
              cursor: loading || !prompt.trim() || !isVideo || !durationOk ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6,
              opacity: loading ? 0.7 : 1,
            }}
          >
            <Wand2 size={13} />
            Gerar V2V (~{creditsEstimate} cr)
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
